import React, { useState, Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Fresh colorful badge styling (no white or gray backgrounds)
const baseBadgeClasses = "inline-flex items-center justify-center px-2.5 py-1 rounded-md text-[11px] font-medium whitespace-nowrap transition-all duration-200";

const getPriorityBadgeClass = (priority) => {
  if (!priority) return `${baseBadgeClasses} bg-indigo-100/50 text-indigo-700 ring-1 ring-inset ring-indigo-200/50`;
  
  switch (priority.toLowerCase()) {
    case 'very high':
      return `${baseBadgeClasses} bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200/50`;
    case 'high':
      return `${baseBadgeClasses} bg-orange-100 text-orange-700 ring-1 ring-inset ring-orange-200/50`;
    case 'medium':
      return `${baseBadgeClasses} bg-amber-100 text-amber-800 ring-1 ring-inset ring-amber-200/50`;
    case 'low':
      return `${baseBadgeClasses} bg-sky-100 text-sky-700 ring-1 ring-inset ring-sky-200/50`;
    default:
      return `${baseBadgeClasses} bg-indigo-100/50 text-indigo-700 ring-1 ring-inset ring-indigo-200/50`;
  }
};

const getStatusBadgeClass = (status) => {
  if (!status) return `${baseBadgeClasses} bg-indigo-100/50 text-indigo-700 ring-1 ring-inset ring-indigo-200/50`;
  
  switch (status.toLowerCase()) {
    case 'active':
      return `${baseBadgeClasses} bg-emerald-100 text-emerald-700 ring-1 ring-inset ring-emerald-200/50`;
    case 'inactive':
      return `${baseBadgeClasses} bg-indigo-100/50 text-indigo-700 ring-1 ring-inset ring-indigo-200/50`;
    case 'completed':
      return `${baseBadgeClasses} bg-blue-100 text-blue-700 ring-1 ring-inset ring-blue-200/50`;
    case 'on hold':
      return `${baseBadgeClasses} bg-amber-100 text-amber-700 ring-1 ring-inset ring-amber-200/50`;
    default:
      return `${baseBadgeClasses} bg-indigo-100/50 text-indigo-700 ring-1 ring-inset ring-indigo-200/50`;
  }
};

const getLeadStatusBadgeClass = (status) => {
  if (!status) return `${baseBadgeClasses} bg-indigo-100/50 text-indigo-700 ring-1 ring-inset ring-indigo-200/50`;
  
  switch (status.toLowerCase()) {
    case 'open':
      return `${baseBadgeClasses} bg-sky-100 text-sky-700 ring-1 ring-inset ring-sky-200/50`;
    case 'pending_proposal':
      return `${baseBadgeClasses} bg-amber-100 text-amber-700 ring-1 ring-inset ring-amber-200/50`;
    case 'approved_proposal_owner':
      return `${baseBadgeClasses} bg-emerald-100 text-emerald-700 ring-1 ring-inset ring-emerald-200/50`;
    case 'rejected_proposal_owner':
    case 'customer_rejected':
      return `${baseBadgeClasses} bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200/50`;
    case 'customer_approved':
      return `${baseBadgeClasses} bg-emerald-100 text-emerald-700 ring-1 ring-inset ring-emerald-200/50`;
    default:
      return `${baseBadgeClasses} bg-indigo-100/50 text-indigo-700 ring-1 ring-inset ring-indigo-200/50`;
  }
};

const getTrackingStatusBadgeClass = (status) => {
  if (!status) return `${baseBadgeClasses} bg-indigo-100/50 text-indigo-700 ring-1 ring-inset ring-indigo-200/50`;
  
  switch (status.toLowerCase()) {
    case 'new':
      return `${baseBadgeClasses} bg-emerald-100 text-emerald-700 ring-1 ring-inset ring-emerald-200/50`;
    case 'waiting':
      return `${baseBadgeClasses} bg-amber-100 text-amber-700 ring-1 ring-inset ring-amber-200/50`;
    case 'recently_sent':
      return `${baseBadgeClasses} bg-blue-100 text-blue-700 ring-1 ring-inset ring-blue-200/50`;
    case 'critical':
      return `${baseBadgeClasses} bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200/50`;
    case 'follow_up':
      return `${baseBadgeClasses} bg-violet-100 text-violet-700 ring-1 ring-inset ring-violet-200/50`;
    case 'converted':
      return `${baseBadgeClasses} bg-emerald-100 text-emerald-700 ring-1 ring-inset ring-emerald-200/50`;
    case 'lost':
      return `${baseBadgeClasses} bg-indigo-100/50 text-indigo-700 ring-1 ring-inset ring-indigo-200/50`;
    default:
      return `${baseBadgeClasses} bg-indigo-100/50 text-indigo-700 ring-1 ring-inset ring-indigo-200/50`;
  }
};

const getProjectStatusBadgeClass = (status) => {
  if (!status) return `${baseBadgeClasses} bg-indigo-100/50 text-indigo-700 ring-1 ring-inset ring-indigo-200/50`;
  
  switch (status.toLowerCase()) {
    case 'active':
      return `${baseBadgeClasses} bg-emerald-100 text-emerald-700 ring-1 ring-inset ring-emerald-200/50`;
    case 'completed':
      return `${baseBadgeClasses} bg-blue-100 text-blue-700 ring-1 ring-inset ring-blue-200/50`;
    case 'archived':
      return `${baseBadgeClasses} bg-indigo-100/50 text-indigo-700 ring-1 ring-inset ring-indigo-200/50`;
    case 'on_hold':
      return `${baseBadgeClasses} bg-amber-100 text-amber-700 ring-1 ring-inset ring-amber-200/50`;
    case 'cancelled':
      return `${baseBadgeClasses} bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200/50`;
    case 'ready_to_schedule':
      return `${baseBadgeClasses} bg-purple-100 text-purple-700 ring-1 ring-inset ring-purple-200/50`;
    default:
      return `${baseBadgeClasses} bg-indigo-100/50 text-indigo-700 ring-1 ring-inset ring-indigo-200/50`;
  }
};

const CustomSelect = ({ value, options, onChange, type }) => {
  const [buttonRect, setButtonRect] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = React.useRef(null);
  
  const handleChange = (newValue) => {
    const processedValue = ['leadStatus', 'projectStatus', 'status'].includes(type) ? 
      newValue.toLowerCase() : newValue.toLowerCase();
    onChange(processedValue);
  };

  const formatDisplayValue = (value, type) => {
    if (!value) return '';
    
    if (type === 'leadStatus') {
      return value.split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    
    if (type === 'priority') {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
    
    if (type === 'projectStatus') {
      return value.split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    
    if (type === 'trackingStatus') {
      return value.split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    
    return value;
  };

  const getBadgeClass = (value, type) => {
    if (type === 'priority') return getPriorityBadgeClass(value);
    if (type === 'projectStatus') return getProjectStatusBadgeClass(value);
    if (type === 'status') return getStatusBadgeClass(value);
    if (type === 'trackingStatus') return getTrackingStatusBadgeClass(value);
    return getLeadStatusBadgeClass(value);
  };

  const calculatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 180;
      const dropdownHeight = options.length * 36 + 16;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 12;
      
      let top = rect.bottom + 8;
      let left = rect.left;
      
      if (left + dropdownWidth > viewportWidth - padding) {
        left = rect.right - dropdownWidth;
        if (left < padding) {
          left = viewportWidth - dropdownWidth - padding;
        }
      }
      
      if (left < padding) {
        left = padding;
      }
      
      if (top + dropdownHeight > viewportHeight - padding) {
        top = Math.max(padding, rect.top - dropdownHeight - 8);
      }
      
      setButtonRect(rect);
      setDropdownPosition({ top, left });
    }
  };

  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <Listbox value={value} onChange={handleChange}>
        {({ open }) => (
          <Fragment>
            <Listbox.Button 
              ref={buttonRef}
              onClick={calculatePosition}
              className={`${getBadgeClass(value, type)} cursor-pointer text-left listbox-button hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 focus:ring-offset-indigo-50 group shadow-sm`}
            >
              <span className="flex items-center gap-1.5">
                {formatDisplayValue(value, type)}
                <svg className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none">
                  <path d="M19 9l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            </Listbox.Button>

            {buttonRect && createPortal(
              <Transition
                show={open}
                enter="transition duration-150 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-100 ease-in"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
              >
                <div 
                  className="fixed z-[9999]"
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Listbox.Options 
                    static 
                    className="py-1.5 rounded-xl bg-indigo-50/95 shadow-xl shadow-indigo-900/10 ring-1 ring-indigo-200/60 focus:outline-none min-w-[160px] listbox-options flex flex-col overflow-hidden backdrop-blur-md"
                  >
                    {options.map((option) => (
                      <Listbox.Option
                        key={option}
                        value={option}
                        className={({ active, selected }) => `
                          cursor-pointer select-none px-3 py-2 transition-colors duration-150 flex items-center mx-1.5 rounded-lg my-0.5
                          ${active ? 'bg-indigo-100/80' : 'bg-transparent'}
                          ${selected ? 'bg-indigo-200/60' : ''}
                        `}
                      >
                        {({ selected }) => (
                          <div className="flex items-center justify-between w-full">
                            <span className={getBadgeClass(option, type)}>
                              {formatDisplayValue(option, type)}
                            </span>
                            {selected && (
                              <svg className="w-4 h-4 text-indigo-600" viewBox="0 0 24 24" fill="none">
                                <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </div>
              </Transition>,
              document.body
            )}
          </Fragment>
        )}
      </Listbox>
    </div>
  );
};

const SortableHeader = ({ column, onSort, sortConfig, getSortIcon }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <th 
      ref={setNodeRef}
      style={style}
      key={column.id} 
      className={`
        px-6 py-4 text-left text-[13px] font-bold text-indigo-900/60 uppercase tracking-wider select-none group 
        transition-colors duration-200 hover:bg-indigo-100/50 border-b border-indigo-200/60
        ${isDragging ? 'z-50 bg-indigo-100/90 shadow-xl rounded-lg ring-1 ring-indigo-300' : ''}
      `}
    >
      <div className="flex items-center gap-2">
        <div 
          {...attributes} 
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-indigo-400 hover:text-indigo-700 hover:bg-indigo-200/50 rounded-md transition-all opacity-0 group-hover:opacity-100"
          title="Drag to reorder columns"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
            <path d="M8 6h8M8 12h8M8 18h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div 
          className="cursor-pointer flex-1 flex items-center gap-1.5 hover:text-indigo-800 transition-colors"
          onClick={() => onSort && onSort(column.key)}
        >
          {column.label}
          <span className="text-indigo-400 flex items-center justify-center w-4 h-4">
            {getSortIcon(column.key)}
          </span>
        </div>
      </div>
    </th>
  );
};

const Table = ({ 
  columns = [],
  data,
  onSort,
  onCheckAll,
  onCheckRow,
  selectedRows = [],
  sortConfig,
  onUpdateCell,
  onRowClick,
  onColumnReorder
}) => {
  const [orderedColumns, setOrderedColumns] = useState(columns);

  React.useEffect(() => {
    setOrderedColumns(columns);
  }, [columns]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = orderedColumns.findIndex(col => col.id === active.id);
      const newIndex = orderedColumns.findIndex(col => col.id === over.id);
      
      const newColumns = arrayMove(orderedColumns, oldIndex, newIndex);
      setOrderedColumns(newColumns);
      
      if (onColumnReorder) {
        onColumnReorder(newColumns);
      }
    }
  };

  const priorityOptions = ['very high', 'high', 'medium', 'low'];
  const leadStatusOptions = [
    'open',
    'pending_proposal',
    'approved_proposal_owner',
    'rejected_proposal_owner',
    'customer_rejected'
  ];
  const trackingStatusOptions = [
    'new',
    'waiting',
    'recently_sent',
    'critical',
    'follow_up',
    'converted',
    'lost'
  ];
  const projectStatusOptions = [
    'active',
    'ready_to_schedule',
    'on_hold',
    'completed',
    'cancelled',
    'archived'
  ];
  const statusOptions = ['active', 'inactive', 'completed', 'on hold'];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return (
        <svg className="w-3.5 h-3.5 text-indigo-300 opacity-0 group-hover:opacity-100 transition-all duration-200" viewBox="0 0 24 24" fill="none">
          <path d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }

    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 text-indigo-600" viewBox="0 0 24 24" fill="none">
        <path d="M8 12l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ) : (
      <svg className="w-4 h-4 text-indigo-600" viewBox="0 0 24 24" fill="none">
        <path d="M8 12l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  };

  const renderEditableCell = (row, column) => {
    const value = row[column.key];

    if (column.key === 'priority') {
      return (
        <CustomSelect
          value={value}
          options={priorityOptions}
          onChange={(newValue) => onUpdateCell(row.id, column.key, newValue)}
          type="priority"
        />
      );
    }

    if (column.key === 'leadStatus') {
      return (
        <CustomSelect
          value={value}
          options={leadStatusOptions}
          onChange={(newValue) => onUpdateCell(row.id, column.key, newValue)}
          type="leadStatus"
        />
      );
    }
    
    if (column.key === 'trackingStatus') {
      return (
        <CustomSelect
          value={value}
          options={trackingStatusOptions}
          onChange={(newValue) => onUpdateCell(row.id, column.key, newValue)}
          type="trackingStatus"
        />
      );
    }
    
    if (column.key === 'projectStatus') {
      return (
        <CustomSelect
          value={value}
          options={projectStatusOptions}
          onChange={(newValue) => onUpdateCell(row.id, column.key, newValue)}
          type="projectStatus"
        />
      );
    }
    
    if (column.key === 'status') {
      return (
        <CustomSelect
          value={value}
          options={statusOptions}
          onChange={(newValue) => onUpdateCell(row.id, column.key, newValue)}
          type="status"
        />
      );
    }

    if (column.key === 'mapLink') {
      return (
        <a 
          href={value} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:text-indigo-900 bg-indigo-100/50 hover:bg-indigo-200/50 rounded-lg transition-all duration-200 border border-indigo-200/50 shadow-sm"
        >
          <svg className="w-4 h-4 text-indigo-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6-3l5.447 2.724A1 1 0 0121 7.618v10.764a1 1 0 01-1.447.894L15 17m0-13l-6 3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          View Map
        </a>
      );
    }

    if (column.key === 'createdAt' || column.key === 'lastModified') {
      return (
        <span className="text-indigo-900/60 font-medium">
          {formatDate(value)}
        </span>
      );
    }

    return (
      <span className="text-indigo-950 font-semibold whitespace-nowrap">
        {value}
      </span>
    );
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="bg-[#f8faff] rounded-xl shadow-lg shadow-indigo-900/5 ring-1 ring-indigo-100 overflow-hidden">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse">
            <thead className="bg-[#f0f4ff]/90 backdrop-blur-md relative z-20">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-indigo-900/70 uppercase tracking-wider sticky left-0 bg-[#f0f4ff]/90 backdrop-blur-md border-b border-indigo-200/60 w-12">
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      className="w-4 h-4 rounded border-indigo-300 bg-indigo-50 text-indigo-600 focus:ring-indigo-500/30 transition-all duration-200 cursor-pointer shadow-sm"
                      onChange={onCheckAll}
                      checked={selectedRows.length === data.length && data.length > 0}
                    />
                  </div>
                </th>
                <SortableContext items={orderedColumns.map(col => col.id)} strategy={horizontalListSortingStrategy}>
                  {orderedColumns.map((column) => (
                    <SortableHeader
                      key={column.id}
                      column={column}
                      onSort={onSort}
                      sortConfig={sortConfig}
                      getSortIcon={getSortIcon}
                    />
                  ))}
                </SortableContext>
              </tr>
            </thead>
            <tbody className="bg-[#f8faff] divide-y divide-indigo-100/50">
              {data.map((row, rowIndex) => {
                const isSelected = selectedRows.includes(row.id);
                
                return (
                  <tr 
                    key={rowIndex} 
                    className={`
                      group transition-colors duration-200 cursor-pointer
                      ${isSelected ? 'bg-indigo-100/70 hover:bg-indigo-200/60' : 'hover:bg-indigo-50/80'}
                    `}
                    onClick={(e) => {
                      if (e.target.type === 'checkbox' || 
                          e.target.closest('.listbox-button') || 
                          e.target.closest('.listbox-options') ||
                          e.target.tagName.toLowerCase() === 'a') {
                        return;
                      }
                      onRowClick && onRowClick(row);
                    }}
                  >
                    <td className={`
                      px-6 py-4 whitespace-nowrap sticky left-0 z-10 transition-colors duration-200 border-b border-indigo-100/50 group-last:border-0
                      ${isSelected ? 'bg-[#eef2ff] group-hover:bg-[#e0e7ff]' : 'bg-[#f8faff] group-hover:bg-[#f3f6ff]'}
                    `}>
                      <div className="flex items-center">
                        <input 
                          type="checkbox" 
                          className="w-4 h-4 rounded border-indigo-300 bg-indigo-50 text-indigo-600 focus:ring-indigo-500/30 transition-all duration-200 cursor-pointer shadow-sm"
                          checked={isSelected}
                          onChange={() => onCheckRow && onCheckRow(row.id)}
                        />
                      </div>
                    </td>
                    {orderedColumns.map((column, colIndex) => (
                      <td 
                        key={colIndex} 
                        className="px-6 py-4 whitespace-nowrap text-sm border-b border-indigo-100/50 group-last:border-0"
                      >
                        {renderEditableCell(row, column)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data.length === 0 && (
            <div className="py-16 flex flex-col items-center justify-center text-indigo-900/50 bg-[#f8faff]">
              <svg className="w-12 h-12 text-indigo-200 mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-sm font-semibold text-indigo-900/60">No data available</p>
            </div>
          )}
        </div>
      </div>
    </DndContext>
  );
};

export default Table;
